/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import useAlert from 'utils/alert';
import { capitalize } from 'utils/helpers';
import useDeleteLedger from 'utils/ledger/use-delete-ledger-entry';
import { AccountingType } from 'v2-types/order';

import { initialState, reducerModals } from '../components/prototype-new-table-charges/services';
import { columnsDataSet } from '../components/table-charges-kmln/services';
import { getOrder } from './querys';
import { useAccountingUpdateSocketEvent } from "hooks/useAccountingUpdateFromSocket";

const useChargesKMLN = (allowedLetters:string[]) => {
  const { id: orderId } = useParams<{ id: string }>();
  const [accounting, setAccounting] = useState<AccountingType[]>([]);
  const [state, dispatch] = useReducer(reducerModals, initialState);
  const [itemSelected, setSelectItem] = useState<columnsDataSet | null>(null);
  const showAlert = useAlert();
  const { t } = useTranslation();

  const queryCache = useQueryClient();
  const reloadAccounting = () => queryCache.invalidateQueries(['accounting-order', orderId]);
  useAccountingUpdateSocketEvent({
    callback: () => reloadAccounting()
  });

  const getOrderAccounting = useQuery(['accounting-order', orderId],
	  () => getOrder(orderId, { accounting: `/[${allowedLetters.join('')}]/` }),
	  {
      enabled: !!orderId,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          if (Array.isArray(allowedLetters)) {
            setAccounting(data.getOrder?.accounting?.filter((f) => allowedLetters.some((g) => f.letter?.includes(g))) || []);
          }
        }
      }
	  },);

  const { deleteEntry, deleteEntryResponse } = useDeleteLedger((data) => {
	  deleteEntryResponse.reset();
	  showAlert(capitalize(t('dialogs:order-updated')), 'success');
  },
  () => { showAlert('there was an error deleting the entry', 'error'); }
  );

  const handleDelete = useCallback((item:columnsDataSet) => {
	  deleteEntry({
      id: orderId,
      accountingId: item.id
	  });
  }, [deleteEntry, orderId]);

  const handleEditModal = (item: columnsDataSet) => {
    const type = item.chargeData?._id.length > 10 ? 'edit' : 'add';
    let data = ({ } as AccountingType);

    if (type === "add" && item && item.chargeData) {
      data.letter = item.chargeData.letter;
      data.number = item.chargeData.number;
    }

    if (type === "edit") {
      data = { ...item.chargeData };
    }

    dispatch({ type, data });
  };

  const handleUpdateAccounting = (acc:AccountingType[]) => {
    setAccounting(acc);
  };

  const handleSelectItem = (item:columnsDataSet) => {
    setSelectItem(item);
  };

  const handleAddModal = (type:string | null) => {
    dispatch({ type });
  };

  useEffect(() => {
	  if (getOrderAccounting.isLoading || getOrderAccounting.isFetching) {
      showAlert('Updating...', 'info');
	  }
  }, [getOrderAccounting.isLoading, getOrderAccounting.isFetching]);

  useEffect(() => {
	  if (deleteEntryResponse.isLoading) {
      showAlert('Deleting Entry...', 'info');
	  }
  }, [deleteEntryResponse.isLoading]);

  const openAddModal = state.type === 'add' || state.type === 'edit';

  return {
	  dataCharges: accounting,
	  loading: getOrderAccounting.isLoading,
	  setAccounting,
	  openAddModal,
	  itemSelected,
	  handleEditModal,
	  handleDelete,
	  handleUpdateAccounting,
	  handleAddModal,
	  handleSelectItem,
	  reloadAccounting,
	  state
  };
};

export default useChargesKMLN;
